/**
 * SYSTEM SETTINGS MODULE — Retail Locations source.
 *
 * Chooses where the Retail Product Finder's store list comes from, then shows
 * only the controls that source actually needs:
 *
 *   Depletions   → a time window (stores with no sales in the window drop off
 *                  the public map). No sheet to pick.
 *   Google Sheet → the standard GoogleSheetsPanel, same as Depletions/Inventory.
 *
 * Deliberately a radio pair rather than a checkbox: the two sources are
 * mutually exclusive, and a checkbox labelled "use depletions data" leaves the
 * unchecked state ambiguous (no source at all, or a sheet?). Each option is a
 * tile that owns its own dependent controls, so the mutual exclusivity — and
 * which controls belong to which source — is visible rather than implied.
 *
 * Layout lives in RetailLocationsPanel.css; read its header before adding
 * markup here, because `.settings-card` styles bare `label`/`input` in a way
 * that fights inline radio rows.
 */

import { useCallback, useEffect, useState } from "react";
import GoogleSheetsPanel from "./GoogleSheetsPanel.jsx";
import {
  getRetailSource,
  saveRetailSource,
  rebuildOutletsFromDepletions,
  RECENCY_OPTIONS,
} from "../utils/retailSourceService.js";
import "./RetailLocationsPanel.css";

export default function RetailLocationsPanel() {
  const [cfg, setCfg]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy]   = useState(false);
  const [err, setErr]     = useState(null);
  const [rebuilding, setRebuilding] = useState(false);
  const [report, setReport] = useState(null);

  useEffect(() => {
    getRetailSource()
      .then(setCfg)
      .catch((e) => setErr(e.message))
      .finally(() => setLoading(false));
  }, []);

  const patch = useCallback(async (next) => {
    setBusy(true);
    setErr(null);
    try {
      setCfg(await saveRetailSource(next));
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  }, []);

  const rebuild = useCallback(async () => {
    setRebuilding(true);
    setErr(null);
    setReport(null);
    try {
      setReport(await rebuildOutletsFromDepletions());
    } catch (e) {
      setErr(e.message);
    } finally {
      setRebuilding(false);
    }
  }, []);

  if (loading) return <p className="muted">Loading…</p>;

  const source = cfg?.source_kind ?? null;
  // `null` is a real, savable choice ("no cutoff"), so the select is keyed on a
  // string and mapped back — otherwise null and "unset" collide in the DOM.
  const recencyValue = cfg?.recency_months == null ? "none" : String(cfg.recency_months);

  // The cutoff applies to BOTH sources — it means the same thing in each ("hide a
  // store we haven't seen for this long"), only the evidence differs: depletions
  // means "no sales", a sheet means "no longer in the file". Same control, same
  // stored column, so it's rendered from one place with per-source helper text.
  const renderCutoff = (help) => (
    <>
      <label className="rl-field">
        <span>How far back to look</span>
        <select
          value={recencyValue}
          disabled={busy}
          onChange={(e) =>
            patch({
              recency_months: e.target.value === "none" ? null : Number(e.target.value),
            })
          }
        >
          {RECENCY_OPTIONS.map((o) => (
            <option key={o.label} value={o.value == null ? "none" : String(o.value)}>
              {o.label}
            </option>
          ))}
        </select>
      </label>
      <p className="muted gs-foot">{help}</p>
    </>
  );

  return (
    <div className="gs-panel rl-panel">
      <h3 className="gs-title">Retail Locations</h3>
      <p className="muted rl-intro">
        Where the “where to buy” store list comes from.
      </p>

      <div className="rl-options">
        <div className={`rl-choice${source === "depletions" ? " is-selected" : ""}`}>
          <label className="rl-option">
            <input
              type="radio"
              name="retail-source"
              checked={source === "depletions"}
              disabled={busy}
              onChange={() => patch({ source_kind: "depletions" })}
            />
            <span>Use the data from the Depletions module</span>
          </label>

          {source === "depletions" && (
            <div className="rl-sub">
              {renderCutoff(
                "A store with no depletions inside this window drops off the public map. " +
                "Depletions are recorded by month, so these windows round to whole months."
              )}

              <div className="rl-actions">
                <button
                  className="btn secondary"
                  disabled={rebuilding || busy}
                  onClick={rebuild}
                >
                  {rebuilding ? "Rebuilding…" : "Rebuild store list now"}
                </button>
              </div>
              <p className="muted gs-foot">
                Runs automatically after each depletions sync. Press this to rebuild
                immediately — it only ever adds and updates stores, never deletes them.
              </p>

              {report && (
                <div className="muted gs-foot rl-report">
                  {report.outlets_created > 0
                    ? `Found ${report.outlets_created} new ${report.outlets_created === 1 ? "store" : "stores"}. `
                    : "No new stores. "}
                  {report.rows_linked > 0 && `Linked ${report.rows_linked} depletion rows. `}
                  {report.outlets_retired > 0 && `Hid ${report.outlets_retired} no longer in your data. `}
                  {report.rows_unkeyed > 0 && (
                    <>
                      <br />
                      {report.rows_unkeyed} row{report.rows_unkeyed === 1 ? "" : "s"} named a
                      store but had no address or city, so {report.rows_unkeyed === 1 ? "it" : "they"}{" "}
                      couldn’t be placed. Add an Address or City column to your
                      depletions sheet to include {report.rows_unkeyed === 1 ? "it" : "them"}.
                    </>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <div className={`rl-choice${source === "google_sheet" ? " is-selected" : ""}`}>
          <label className="rl-option">
            <input
              type="radio"
              name="retail-source"
              checked={source === "google_sheet"}
              disabled={busy}
              onChange={() => patch({ source_kind: "google_sheet" })}
            />
            <span>Use a separate Google Sheet of store addresses</span>
          </label>

          {source === "google_sheet" && (
            <div className="rl-sub">
              <GoogleSheetsPanel
                dataset="retail_locations"
                label="Retail Locations — Google Sheet"
              />
              {renderCutoff(
                "A store that stops appearing in your sheet stays on the map until it has " +
                "been missing this long — so one partial file, or a filter left applied " +
                "before saving, can’t empty your map. “No cutoff” keeps every store until " +
                "you hide it yourself."
              )}
            </div>
          )}
        </div>
      </div>

      {source === null && (
        <p className="muted rl-empty">
          Pick a source to start building your store list.
        </p>
      )}

      {err && <p className="error rl-err">{err}</p>}
    </div>
  );
}
